import { type CypherResult } from '@neo4j/cypher-builder';
import { APP_SCOPE, QUERY_TYPE, type QueryResultWithLimit } from '@nx/constants';
import { type ParseableError } from '@nx/errors';
import { StoreKit, runQuery } from '@nx/state';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type * as DriverCore from 'neo4j-driver-core';
import { v4 as generateRequestId } from 'uuid';

import {
  type ExploreError,
  UnknownErrors,
  isErrorPayload,
  isTransactionTimeoutError,
} from '../../../services/errors/errors';
import { errorMapper } from '../../../services/queries/errorMapper';
import { graphMapper as mapper } from '../../../services/queries/resultMapper';
import { registerAbortFunction, unregisterAbortFunction } from '../../../services/search/abortable-requests-service';
import { checkCypherUpdateClauses, generateCypher } from '../../../services/search/query-generator';
import type { Records } from '../../../services/search/types';
import { trackActionExecution } from '../../../services/tracking';
import { isVersion5OorGreater } from '../../../services/versions/versionUtils';
import type { Node, Relationship } from '../../../types/graph';
import type { SceneAction, TransformedSearchCategory } from '../../../types/perspective';
import type { Nullable } from '../../../types/utility';
import { getServerVersion } from '../../connections/connectionsDuck';
import { showNodesAndRelationships } from '../../graph/graph.thunks';
import { getRelationshipTypes, getVisibleRelationshipTypes } from '../../perspectives/perspectives';
import { getPerspective } from '../../perspectives/perspectives.selector';
import { type RootState } from '../../types';
import { NAME as requests } from '../requests/search-requests.const';
import { clearRequestsThunk } from '../requests/search-requests.thunks';
import { NAME as searchCore } from './search-core.const';
import { selectLockedSuggestions } from './search-core.selectors';
import { getResultsByUniqueNodes } from './search-core.utils';

type FetchDataPayload = {
  cypher: string;
  parameters: Record<string, unknown>;
  timeout: number;
  database?: string;
  recordLimit?: (newRecord: DriverCore.Record) => boolean;
};

type FetchDataResult = {
  requestId: string;
  recordLimitHit?: boolean;
};

export const fetchDataThunk = createAsyncThunk<
  FetchDataResult,
  FetchDataPayload,
  {
    rejectValue: {
      error: ExploreError;
      requestId: string;
    };
  }
>(`${searchCore}/fetchDataThunk`, async (payload: FetchDataPayload, { dispatch, rejectWithValue }) => {
  const requestId = generateRequestId();
  dispatch({ type: `${requests}/createRequest`, payload: requestId });

  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const { cypher, parameters, recordLimit, timeout, database } = payload;
  const isUpdateQuery = checkCypherUpdateClauses(cypher);
  try {
    const query = globalStore.dispatch(
      runQuery({
        parameters,
        recordLimit,
        sessionConfig: { database },
        query: cypher,
        timeout,
        metadata: { appScope: APP_SCOPE.explore, queryType: QUERY_TYPE.UserDirect },
        transactionMode: isUpdateQuery ? 'write' : 'read',
      }),
    );

    registerAbortFunction(requestId, query.abort);
    const queryResult: QueryResultWithLimit = await query.unwrap();
    return {
      recordLimitHit: queryResult.recordLimitHit,
      requestId,
    };
  } catch (error) {
    return rejectWithValue({ error: errorMapper(error), requestId });
  } finally {
    unregisterAbortFunction(requestId);
  }
});

type RunSearchPayload = {
  queryResultLimit: number;
  timeout: number;
  isCaseInsensitive: boolean;
  autoSelectNewNodes: boolean;
  categories: TransformedSearchCategory[];
  isHttpQueryApiEnabled: boolean;
  database?: string;
  cypher?: Nullable<CypherResult>;
  isFullTextSearch?: boolean;
  sanitizedInputText?: string;
};

type RunSearchResult = {
  nodes: Node[];
  relationships: Relationship[];
  hasFacetedResults?: boolean;
  sanitizedInputText?: string;
};

export const runSearchThunk = createAsyncThunk<
  RunSearchResult,
  RunSearchPayload,
  {
    state: RootState;
    rejectValue: {
      error: ExploreError;
      nodesCount?: number;
    };
  }
>(`${searchCore}/runSearch`, async (payload: RunSearchPayload, { dispatch, getState, rejectWithValue }) => {
  await dispatch(clearRequestsThunk());
  const {
    queryResultLimit,
    timeout,
    isCaseInsensitive,
    categories,
    database,
    autoSelectNewNodes,
    isHttpQueryApiEnabled,
    isFullTextSearch = false,
    sanitizedInputText,
  } = payload;
  const state = getState();
  const lockedSuggestions = selectLockedSuggestions(state);
  const isV5OrGreater = isVersion5OorGreater(getServerVersion(state));
  const relationshipTypes = getRelationshipTypes(state);
  const visibleRelationshipTypes = getVisibleRelationshipTypes(state);

  let nodes, relationships, recordLimitHit;
  let totalRecords: Records = { nodes: [], relationships: [] };
  let mappedRecords: Records = { nodes: [], relationships: [] };
  let uniqueNodesIds = new Set<string>();
  let cypherResult: CypherResult;

  try {
    cypherResult =
      payload.cypher ??
      (await generateCypher({
        lockedSuggestions,
        categories,
        visibleRelationshipTypes,
        isCaseInsensitive,
        isHttpQueryApiEnabled,
      }));
  } catch (error) {
    return rejectWithValue({ error: errorMapper(error) });
  }

  const isUpdateQuery = checkCypherUpdateClauses(cypherResult.cypher);

  try {
    const recordLimit = (newRecord: DriverCore.Record) => {
      mappedRecords = mapper([newRecord]);
      const resultsByUniqueNodes = getResultsByUniqueNodes({
        previousRecords: totalRecords,
        newRecords: mappedRecords,
        uniqueNodesIds,
        limit: queryResultLimit,
        isV5OrGreater,
        visibleRelationshipTypes,
        relationshipTypes,
      });

      totalRecords = { ...resultsByUniqueNodes.totalRecords };
      uniqueNodesIds = new Set(resultsByUniqueNodes.uniqueNodesIds);

      return !resultsByUniqueNodes.isAboveLimit;
    };

    const fetchedData = dispatch(
      fetchDataThunk({
        cypher: cypherResult.cypher,
        parameters: cypherResult.params,
        database,
        timeout,
        recordLimit,
      }),
    );
    ({ recordLimitHit } = await fetchedData.unwrap());
    ({ nodes, relationships } = totalRecords);
  } catch (e) {
    const { error } = isErrorPayload(e) ? e : { error: UnknownErrors.UnknownError };
    ({ nodes, relationships } = totalRecords);

    if (isTransactionTimeoutError(error)) {
      void dispatch(
        showNodesAndRelationships({
          nodes,
          relationships,
          autoSelectNewNodes,
          recordLimitHit,
          isUpdateQuery,
          error,
        }),
      );
    }

    return rejectWithValue({ error, nodesCount: nodes.length });
  }

  const hasFacetedResults = isFullTextSearch && recordLimitHit;
  if (!hasFacetedResults) {
    void dispatch(
      showNodesAndRelationships({
        nodes,
        relationships,
        autoSelectNewNodes,
        recordLimitHit,
        isUpdateQuery,
      }),
    );
  }

  return {
    nodes,
    relationships,
    hasFacetedResults,
    sanitizedInputText,
  };
});

type SceneActionParameters = {
  nodes: (DriverCore.Integer | string)[];
  relationships: (DriverCore.Integer | string)[];
};

type RunSceneActionPayload = {
  action: SceneAction;
  params: SceneActionParameters;
  queryResultLimit: number;
  timeout: number;
  autoSelectNewNodes: boolean;
  categories: TransformedSearchCategory[];
  database?: string;
};

export const runSceneActionThunk = createAsyncThunk<
  void,
  RunSceneActionPayload,
  {
    state: RootState;
    rejectValue: {
      error: ParseableError;
      nodesCount?: number;
    };
  }
>(`${searchCore}/runSceneAction`, async (payload: RunSceneActionPayload, { dispatch, getState, rejectWithValue }) => {
  const {
    action: { id, cypher },
    params,
    queryResultLimit,
    timeout,
    database,
    autoSelectNewNodes,
  } = payload;
  const state = getState();
  const perspective = getPerspective(state);
  const relationshipTypes = getRelationshipTypes(state);
  const visibleRelationshipTypes = getVisibleRelationshipTypes(state);

  const isV5OrGreater = isVersion5OorGreater(getServerVersion(state));
  let nodes, relationships, recordLimitHit;
  const isUpdateQuery = checkCypherUpdateClauses(cypher);

  let totalRecords: Records = { nodes: [], relationships: [] };
  let mappedRecords: Records = { nodes: [], relationships: [] };
  let uniqueNodesIds = new Set<string>();

  try {
    const recordLimit = (newRecord: DriverCore.Record) => {
      mappedRecords = mapper([newRecord]);
      const resultsByUniqueNodes = getResultsByUniqueNodes({
        previousRecords: totalRecords,
        newRecords: mappedRecords,
        uniqueNodesIds,
        limit: queryResultLimit,
        isV5OrGreater,
        visibleRelationshipTypes,
        relationshipTypes,
      });

      totalRecords = { ...resultsByUniqueNodes.totalRecords };
      uniqueNodesIds = new Set(resultsByUniqueNodes.uniqueNodesIds);

      return !resultsByUniqueNodes.isAboveLimit;
    };

    const fetchedData = dispatch(fetchDataThunk({ cypher, parameters: params, database, timeout, recordLimit }));
    ({ recordLimitHit } = await fetchedData.unwrap());
    ({ nodes, relationships } = totalRecords);
    trackActionExecution(
      id,
      perspective?.id ?? '',
      params.nodes?.length,
      params.relationships?.length,
      isUpdateQuery,
      nodes.length,
      relationships.length,
    );
  } catch (e) {
    const { error } = isErrorPayload(e) ? e : { error: UnknownErrors.UnknownError };
    ({ nodes, relationships } = totalRecords);

    if (isTransactionTimeoutError(error)) {
      void dispatch(
        showNodesAndRelationships({
          nodes,
          relationships,
          autoSelectNewNodes,
          recordLimitHit,
          isUpdateQuery,
          error,
        }),
      );
    }

    return rejectWithValue({ error, nodesCount: nodes.length });
  }

  void dispatch(
    showNodesAndRelationships({
      nodes,
      relationships,
      autoSelectNewNodes,
      recordLimitHit,
      isUpdateQuery,
    }),
  );
});
